import React, { useState, useEffect } from 'react';
import { adminAPI } from '../../services/api';
import './Modal.css';

interface ViewUserModalProps {
  isOpen: boolean;
  onClose: () => void;
  user: any;
}

const ViewUserModal: React.FC<ViewUserModalProps> = ({ isOpen, onClose, user }) => {
  const [userDetails, setUserDetails] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [password, setPassword] = useState('');

  useEffect(() => {
    if (isOpen && user) {
      fetchUserDetails();
    }
  }, [isOpen, user]);

  const fetchUserDetails = async () => {
    setLoading(true);
    try {
      let details;
      if (user.role === 'dealer' && user.dealer_id) {
        details = await adminAPI.getDealer(user.dealer_id);
      } else {
        // For managers and corporate, use the user data we already have
        details = { success: true, ...user };
      }

      if (details.success || details) {
        setUserDetails(details);
      }
    } catch (err) {
      console.error('Error fetching user details:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleShowPassword = async () => {
    if (showPassword) {
      setShowPassword(false);
      setPassword('');
      return;
    }

    try {
      const data = await adminAPI.resetPassword(user.user_id);
      if (data.success) {
        setPassword(data.new_password);
        setShowPassword(true);
        alert('Password has been reset! The new password is now displayed.');
      }
    } catch (err: any) {
      alert(`Error resetting password: ${err.message}`);
    }
  };

  const handleCopyPassword = () => {
    if (password) {
      navigator.clipboard.writeText(password);
      alert('Password copied to clipboard!');
    }
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content modal-large" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>
            <span className="material-icons">
              {user.role === 'dealer' ? 'store' : 'person'}
            </span>
            View User Details
          </h2>
          <button className="modal-close" onClick={onClose}>
            <span className="material-icons">close</span>
          </button>
        </div>

        <div className="modal-body">
          {loading ? (
            <div style={{ textAlign: 'center', padding: '2rem' }}>
              <div className="loading-spinner"></div>
              <p>Loading user details...</p>
            </div>
          ) : userDetails ? (
            <>
              {/* Basic Information */}
              <div className="form-section">
                <h3>Basic Information</h3>
                <div className="info-display">
                  <div className="info-row">
                    <span className="info-label">User ID:</span>
                    <span className="info-value">{user.user_id}</span>
                  </div>
                  <div className="info-row">
                    <span className="info-label">Username:</span>
                    <span className="info-value">{user.username}</span>
                  </div>
                  <div className="info-row">
                    <span className="info-label">Email:</span>
                    <span className="info-value">{user.email || 'N/A'}</span>
                  </div>
                  <div className="info-row">
                    <span className="info-label">Role:</span>
                    <span className="info-value">
                      {user.role === 'corporate' && 'Corporate'}
                      {user.role === 'regional' && 'Regional Manager'}
                      {user.role === 'district' && 'District Manager'}
                      {user.role === 'dealer' && 'Dealer'}
                    </span>
                  </div>
                  <div className="info-row">
                    <span className="info-label">Status:</span>
                    <span className="info-value">
                      <span className={`status-badge ${user.is_active ? 'status-active' : 'status-inactive'}`}>
                        {user.is_active ? 'Active' : 'Inactive'}
                      </span>
                    </span>
                  </div>
                </div>
              </div>

              {/* Password Section */}
              <div className="form-section">
                <h3>Password</h3>
                <div className="password-view-section">
                  {!showPassword ? (
                    <>
                      <div className="password-blurred">
                        ••••••••••••
                      </div>
                      <button className="btn-secondary" onClick={handleShowPassword}>
                        <span className="material-icons">visibility</span>
                        Reset & View Password
                      </button>
                      <small style={{ display: 'block', marginTop: '0.5rem', color: '#666' }}>
                        Note: Clicking this will reset the user's password and display the new one.
                      </small>
                    </>
                  ) : (
                    <div className="password-display">
                      <div className="password-display-header">
                        <span className="material-icons">key</span>
                        <strong>Current Password</strong>
                      </div>
                      <div className="password-value">
                        <span className="password-text">{password}</span>
                        <button className="copy-button" onClick={handleCopyPassword}>
                          <span className="material-icons">content_copy</span>
                          Copy
                        </button>
                      </div>
                      <button className="btn-secondary" onClick={() => {
                        setShowPassword(false);
                        setPassword('');
                      }}>
                        <span className="material-icons">visibility_off</span>
                        Hide Password
                      </button>
                    </div>
                  )}
                </div>
              </div>

              {/* Dealer-specific Information */}
              {user.role === 'dealer' && (
                <>
                  <div className="form-section">
                    <h3>Dealer Information</h3>
                    <div className="info-display">
                      <div className="info-row">
                        <span className="info-label">Dealer ID:</span>
                        <span className="info-value">{user.dealer_id}</span>
                      </div>
                      <div className="info-row">
                        <span className="info-label">Dealer Name:</span>
                        <span className="info-value">{user.dealer_name || userDetails.dealer_name || 'N/A'}</span>
                      </div>
                    </div>
                  </div>

                  {userDetails.address && (
                    <div className="form-section">
                      <h3>Location Information</h3>
                      <div className="info-display">
                        <div className="info-row">
                          <span className="info-label">Address:</span>
                          <span className="info-value">{userDetails.address}</span>
                        </div>
                        <div className="info-row">
                          <span className="info-label">City, State ZIP:</span>
                          <span className="info-value">
                            {userDetails.city}, {userDetails.state} {userDetails.zip}
                          </span>
                        </div>
                        {userDetails.latitude && userDetails.longitude && (
                          <>
                            <div className="info-row">
                              <span className="info-label">Coordinates:</span>
                              <span className="info-value">
                                {userDetails.latitude.toFixed(6)}, {userDetails.longitude.toFixed(6)}
                              </span>
                            </div>
                            {userDetails.radius_zips_10 && (
                              <div className="info-row">
                                <span className="info-label">Radius ZIPs:</span>
                                <span className="info-value">
                                  10mi: {userDetails.radius_zips_10.length} |
                                  20mi: {userDetails.radius_zips_20?.length || 0} |
                                  30mi: {userDetails.radius_zips_30?.length || 0}
                                </span>
                              </div>
                            )}
                          </>
                        )}
                      </div>
                    </div>
                  )}
                </>
              )}

              {/* Manager-specific Information */}
              {(user.role === 'district' || user.role === 'regional') && (
                <div className="form-section">
                  <h3>Management Information</h3>
                  <div className="info-display">
                    <div className="info-row">
                      <span className="info-label">Manager Name:</span>
                      <span className="info-value">{user.manager_name || 'N/A'}</span>
                    </div>
                    {user.reports_to && (
                      <div className="info-row">
                        <span className="info-label">Reports To:</span>
                        <span className="info-value">{user.reports_to}</span>
                      </div>
                    )}
                    {user.manages && user.manages.length > 0 && (
                      <div className="info-row">
                        <span className="info-label">Manages:</span>
                        <span className="info-value">
                          {user.manages.length} {user.role === 'district' ? 'dealers' : 'district managers'}
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </>
          ) : (
            <p>No user details available</p>
          )}
        </div>

        <div className="modal-footer">
          <button className="btn-secondary" onClick={onClose}>
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default ViewUserModal;
